import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Bell,
  BellOff,
  Loader2,
  UserPlus,
  ShieldAlert,
  Radio,
  Users,
  MessageCircle,
  TrendingUp,
  TrendingDown,
  CalendarClock,
  CalendarX,
} from "lucide-react";
import { api } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { cn } from "@/lib/utils";

const TYPE_ICON = {
  connection: UserPlus,
  connection_request: UserPlus,
  flag: ShieldAlert,
  shield: ShieldAlert,
  channel: Radio,
  circle: Users,
  message: MessageCircle,
  score: TrendingUp,
  trust: TrendingUp,
  subscription_renewal: CalendarClock,
  subscribers_lapsing: TrendingDown,
  event_reminder: CalendarClock,
  event_canceled: CalendarX,
};

function iconFor(type = "") {
  for (const key of Object.keys(TYPE_ICON)) {
    if (type.includes(key)) return TYPE_ICON[key];
  }
  return Bell;
}

function NotificationRow({ n, onOpen }) {
  const Icon = iconFor(n.type || "");
  const title = n.title || n.type?.replace(/[_.]/g, " ") || "Notification";
  const body = n.body || n.text || n.message || n.description || "";
  return (
    <button
      onClick={() => onOpen(n)}
      className={cn(
        "flex w-full items-start gap-3 rounded-2xl border p-4 text-left transition-colors",
        n.read ? "border-white/8 bg-bloq-s1 hover:bg-white/5" : "border-bloq-mint/25 bg-bloq-mint/[0.06] hover:bg-bloq-mint/10",
      )}
      data-testid="notification-row"
    >
      <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", n.read ? "bg-white/5 text-bloq-muted" : "bg-bloq-mint/15 text-bloq-mint")}>
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-semibold capitalize">{title}</span>
          {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-bloq-mint" />}
        </div>
        {body && <p className="mt-0.5 text-sm text-bloq-muted">{body}</p>}
        <span className="mt-1 block text-[11px] text-bloq-muted/70">{timeAgo(n.created_at)}</span>
      </div>
    </button>
  );
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["notifications"], queryFn: api.notifications });

  const readMut = useMutation({
    mutationFn: () => api.readNotifications(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const items = data?.items || [];
  const unread = data?.unread || 0;

  useEffect(() => {
    if (unread > 0) readMut.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unread]);

  const openNotif = (n) => {
    // Prefer an explicit backend deeplink, then a string ref, then construct from the ref object by type.
    const dl = n.deeplink;
    if (typeof dl === "string" && dl.startsWith("/")) return navigate(dl);
    const ref = n.ref;
    if (typeof ref === "string" && ref.startsWith("/")) return navigate(ref);
    const t = n.type || "";
    const r = ref && typeof ref === "object" ? ref : {};
    if (t.includes("subscription_renewal") && r.creator_id) return navigate(`/app/creators/${r.creator_id}`);
    if (t.includes("subscribers_lapsing")) return navigate("/app/creator");
    if ((t.includes("event_reminder") || t.includes("event_canceled")) && r.circle_id) return navigate(`/app/bloqs/${r.circle_id}?info=1`);
    const link = n.link || n.url;
    if (typeof link === "string" && link.startsWith("/")) return navigate(link);
    if (n.conversation_id) return navigate(`/app/chats/${n.conversation_id}`);
    if (n.channel_id) return navigate(`/app/channels/${n.channel_id}`);
    if (n.circle_id) return navigate(`/app/bloqs/${n.circle_id}`);
  };

  return (
    <div className="h-full overflow-y-auto bg-bloq-bg" data-testid="notifications-page">
      <div className="mx-auto max-w-2xl px-5 py-6 sm:px-8">
        <div className="mb-5 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="rounded-full p-1 text-bloq-muted hover:text-white md:hidden" data-testid="notifications-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Notifications</h1>
            <p className="mt-0.5 text-sm text-bloq-muted">Trust events, requests & Shield alerts</p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16 text-bloq-muted"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-3xl border border-white/8 bg-bloq-s1 px-6 py-16 text-center" data-testid="notifications-empty">
            <BellOff className="mx-auto mb-3 h-10 w-10 text-bloq-muted/60" />
            <p className="text-sm text-bloq-muted">You're all caught up — no notifications yet.</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {items.map((n, i) => <NotificationRow key={n.id || i} n={n} onOpen={openNotif} />)}
          </div>
        )}
      </div>
    </div>
  );
}
